import {
  WBButton,
  WBFlex,
  WBTextFieldV2,
  WBTypography,
} from '@admiin-com/ds-web';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { isValidPRN } from '@admiin-com/ds-common';
import { sanitizeReferenceNumber } from '../../../../../helpers/string';
import { getNestedError } from '../utils';
import { Bill } from '../../../PayBillCreateForm';
import { DocumentType } from '@admiin-com/ds-graphql';
import { DateTime } from 'luxon';
import { useClientSelection } from '../../../../../features/hooks/useClientSelection/useClientSelection';
import { BillCreationContext } from '../../../../../features/providers/TaskCreationProvider/TaskCreationProvider';

type TaxPaymentReferenceHistory = {
  referenceNumber?: string | null;
  updatedAt?: string | null;
};

export const SignPayPRN = ({ scannedTax }: { scannedTax: Bill | null }) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();
  const from = watch(`futureTaskInput.from`);
  const documentType = useWatch({ name: 'documentType' });
  const { client } = React.useContext(BillCreationContext) ?? {};
  const { accountantUser } = useClientSelection(client?.id);
  const taxPaymentReferences = accountantUser?.taxPaymentReferences ?? null;
  const isItrLodgement = documentType === DocumentType.ITR;
  const lastReference =
    (taxPaymentReferences?.[isItrLodgement ? 'itr' : 'other'] as
      | TaxPaymentReferenceHistory
      | undefined
      | null) ?? null;
  const lodgementLabel = isItrLodgement
    ? t(DocumentType.ITR, { ns: 'taskbox' })
    : t(
        documentType && documentType !== DocumentType.NONE
          ? documentType
          : 'taxLodgementLabel',
        {
          ns: 'taskbox',
          defaultValue: t('taxLodgementLabel', { ns: 'taskbox' }),
        }
      );
  const formattedReferenceDate = lastReference?.updatedAt
    ? DateTime.fromISO(lastReference.updatedAt).toFormat('d MMM yyyy')
    : null;
  const isTax = React.useMemo(
    () => (from as any)?.metadata?.subCategory === 'TAX',
    [from]
  );
  const handleApplyReference = (reference: string) => {
    const sanitized = sanitizeReferenceNumber(reference);
    setValue(inputs.bpay.referenceNumber.name, sanitized, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };
  const inputs = React.useMemo(
    () => ({
      bpay: {
        referenceNumber: {
          label: isTax
            ? t('paymentReferenceNumber', { ns: 'taskbox' })
            : t('bpayReferenceNumber', { ns: 'contacts' }),
          name: `futureTaskInput.bpayReferenceNumber`,
          type: 'text',
          placeholder: isTax
            ? t('paymentReferenceNumberPlaceholder', { ns: 'taskbox' })
            : t('bpayReferenceNumber', { ns: 'contacts' }),
          defaultValue: '',
          rules: {
            validate: {
              isATOorBpay: (value: string | undefined) => {
                if (!value && isTax) {
                  return t('prnRequired', { ns: 'taskbox' });
                }
                if (isTax && value && !isValidPRN(value)) {
                  return t('prnInvalid', { ns: 'taskbox' });
                }
                if (!value) {
                  return t('bpayReferenceRequired', { ns: 'taskbox' });
                }
                if (value && !/^\d{2,20}$/.test(value)) {
                  return t('bpayReferenceInvalid', { ns: 'taskbox' });
                }
                return true;
              },
            },
          },
        },
      },
    }),
    [t, isTax]
  );

  // React.useEffect(() => {
  //   if (!isTax && !scannedTax?.bpay?.referenceNumber) {
  //     setValue(inputs.bpay.referenceNumber.name, null, {
  //       shouldDirty: false,
  //       shouldTouch: false,
  //     });
  //   }
  // }, [
  //   isTax,
  //   inputs.bpay.referenceNumber.name,
  //   scannedTax?.bpay?.referenceNumber,
  // ]);

  return isTax ? (
    <>
      <WBFlex flexDirection={['column', 'row']} gap={[0, 4]}>
        <Controller
          control={control}
          name={inputs.bpay.referenceNumber.name}
          rules={inputs.bpay.referenceNumber.rules}
          defaultValue={inputs.bpay.referenceNumber.defaultValue}
          render={({ field, formState: { errors } }) => (
            <WBTextFieldV2
              {...field}
              type={inputs.bpay.referenceNumber.type}
              placeholder={inputs.bpay.referenceNumber.placeholder}
              label={inputs.bpay.referenceNumber.label}
              key={field.name}
              fullWidth
              error={!!getNestedError(errors, field.name)}
              helperText={
                (getNestedError(errors, field.name)?.message as string) ?? ''
              }
              onChange={(e) => {
                const sanitized = sanitizeReferenceNumber(e.target.value);
                field.onChange(sanitized);
              }}
              onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                // Strip spaces from pasted text
                const strippedText = sanitizeReferenceNumber(pastedText);
                field.onChange(strippedText);
              }}
            />
          )}
        />
      </WBFlex>
      {lastReference?.referenceNumber && (
        <WBFlex mt={1} flexDirection={['column']} alignItems={['flex-start']}>
          <WBTypography variant="body2" color="text.secondary" mr={1}>
            {formattedReferenceDate
              ? t('lastPrnUsedWithDate', {
                  ns: 'taskbox',
                  lodgementType: lodgementLabel,
                  date: formattedReferenceDate,
                })
              : t('lastPrnUsed', {
                  ns: 'taskbox',
                  lodgementType: lodgementLabel,
                })}
          </WBTypography>
          <WBButton
            type="button"
            size="small"
            variant="text"
            onClick={() =>
              handleApplyReference(lastReference.referenceNumber ?? '')
            }
          >
            {t('useThisPrn', {
              ns: 'taskbox',
              reference: lastReference.referenceNumber,
            })}
          </WBButton>
        </WBFlex>
      )}
    </>
  ) : null;
};
